import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Popover } from 'antd';
import classNames from 'classnames';
import { type UseComboboxStateChange, useCombobox } from 'downshift';
import React, { useRef, useState } from 'react';
import { NavLink, useNavigate, useParams } from 'react-router-dom';
import type { ChildBoard } from '../../../types';
import NewBoardForm from '../NewBoardForm';
import styles from './BoardNav.module.scss';
import { faPlus, faSearch } from '@fortawesome/pro-light-svg-icons';

interface BoardNavPopoverProps {
  onNavigate?: (folderId: string) => void;
  children: React.ReactNode;
  onCreateBoard: Function;
  folders?: ChildBoard[];
}

const BoardNavPopoverComp: React.FC<BoardNavPopoverProps> = ({ children, onNavigate, onCreateBoard, folders }) => {
  const popoverRef = useRef<HTMLDivElement>(null);
  const { boardId = '' } = useParams();
  const [inputValue, setInputValue] = useState('');
  const [showPopover, setShowPopover] = useState(false);
  const [showNewBoardModal, setShowNewBoardModal] = useState(false);
  const navigate = useNavigate();
  const { entityId: inboxId, orgPath } = useParams();
  const getFilteredItems = (boards: ChildBoard[] | undefined) => {
    if (boards) {
      return boards.filter((board: ChildBoard) => board?.name.toLowerCase().startsWith(inputValue.toLowerCase()));
    } else {
      return [];
    }
  };

  const { getMenuProps, getInputProps, getItemProps } = useCombobox({
    inputValue,
    selectedItem: null,
    defaultHighlightedIndex: 0,
    items: getFilteredItems(folders),
    stateReducer: (state, actionAndChanges) => {
      const { changes, type } = actionAndChanges;
      switch (type) {
        case useCombobox.stateChangeTypes.InputKeyDownEnter:
          return {
            ...changes,
            isOpen: true,
            highlightedIndex: state.highlightedIndex,
          };

        default:
          break;
      }
      return { ...changes, isOpen: true };
    },
    onStateChange: ({ inputValue = '', type, selectedItem }: UseComboboxStateChange<ChildBoard>) => {
      switch (type) {
        case useCombobox.stateChangeTypes.InputChange:
          setInputValue(inputValue);
          break;
        case useCombobox.stateChangeTypes.InputKeyDownEnter:
          if (selectedItem) {
            if (onNavigate) {
              onNavigate(selectedItem._id);
            }
            setShowPopover(false);
            navigate(`/${orgPath}/inbox/${inboxId}/board/${selectedItem._id}`);
          }
          break;
        default:
          break;
      }
    },
  });

  const handleVisibleChange = (visible: boolean) => {
    setShowPopover(visible);
  };

  const handleCreateNew = () => {
    setShowNewBoardModal(true);
    setShowPopover(false);
  };

  const handleCreateBoard = async (name: string) => {
    setShowNewBoardModal(false);
    await onCreateBoard(name);
  };

  return (
    <>
      <Popover
        open={showPopover}
        onOpenChange={handleVisibleChange}
        trigger="click"
        placement="bottom"
        align={{
          offset: [8, -12],
        }}
        overlayClassName="ant-popover-no-arrow"
        content={
          <div className={styles.popover} ref={popoverRef}>
            <div className={styles.popoverComboWrapper}>
              <div className={styles.popoverCombo}>
                <input
                  placeholder="Search for boards..."
                  className={styles.popoverInput}
                  autoFocus
                  {...getInputProps({}, { suppressRefError: true })}
                />
                <FontAwesomeIcon className={styles.popoverSearchIcon} icon={faSearch} />
              </div>
            </div>
            <ul className={styles.popoverMenu} {...getMenuProps({}, { suppressRefError: true })}>
              {getFilteredItems(folders).map((item: ChildBoard) => (
                <li
                  key={item._id}
                  className={classNames(styles.popoverItem, {
                    [styles.popoverItem__active]: boardId === item._id,
                    [styles.popoverItem__published]: item.published,
                  })}
                  {...getItemProps}
                >
                  <NavLink
                    className={styles.popoverItemLink}
                    to={`/${orgPath}/inbox/${inboxId}/board/${item._id}`}
                    onClick={() => {
                      if (onNavigate) {
                        onNavigate(item._id);
                      }
                      setShowPopover(false);
                    }}
                  >
                    <div className={styles.popoverItemDot}></div>
                    <div className={styles.popoverItemName}>{item.name}</div>{' '}
                    <div className={styles.popoverItemCount}>{item.addedCount}</div>
                  </NavLink>
                </li>
              ))}
            </ul>
            <ul className={styles.popoverSubmenu}>
              <li className={styles.popoverSubmenuItem} onClick={handleCreateNew}>
                <FontAwesomeIcon icon={faPlus} />
                &nbsp; Create Board
              </li>
            </ul>
          </div>
        }
      >
        {children}
      </Popover>
      <NewBoardForm
        visible={showNewBoardModal}
        onCreate={handleCreateBoard}
        onClose={() => setShowNewBoardModal(false)}
        boards={folders}
      />
    </>
  );
};

const BoardNavPopover = BoardNavPopoverComp;

export default BoardNavPopover;
